import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, IconButton, Collapse, Badge, Typography, TextField } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { layoutCardRadius } from '../theme/colors';

const clearFiltersButtonSx = {
  ml: 1,
  minWidth: 0,
  fontFamily: 'Nunito',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  textTransform: 'none',
};

const ProductsTagFilters = ({
  setTagFilter,
  tagFilter,
  tags,
  numProducts,
  defaultExpanded = false,
  sx,
  promoFilter = false,
  onPromoFilterChange,
  showPromoFilter = false,
}) => {
  const [showFilters, setShowFilters] = useState(defaultExpanded);
  const [chipSearch, setChipSearch] = useState('');

  const selectedIds = useMemo(
    () => new Set(tagFilter.map((tag) => tag.id)),
    [tagFilter]
  );

  const visibleTags = useMemo(() => {
    const query = chipSearch.trim().toLowerCase();

    const isVisible = (tag) =>
      selectedIds.has(tag.id) ||
      !query ||
      tag.name.toLowerCase().includes(query);

    const selected = tagFilter
      .map((tag) => tags.find((catalogTag) => catalogTag.id === tag.id))
      .filter(Boolean)
      .filter(isVisible);

    const unselected = tags.filter(
      (tag) => !selectedIds.has(tag.id) && isVisible(tag)
    );

    return [...selected, ...unselected];
  }, [tags, chipSearch, selectedIds, tagFilter]);

  const isTagSelected = (tag) => selectedIds.has(tag.id);
  const hasActiveFilters = tagFilter.length > 0 || Boolean(promoFilter);

  const handleTagClick = (tag) => {
    if (isTagSelected(tag)) {
      setTagFilter(tagFilter.filter((selectedTag) => selectedTag.id !== tag.id));
      return;
    }

    setTagFilter([...tagFilter, tag]);
  };

  const handleClearFilters = () => {
    setTagFilter([]);
    setChipSearch('');
    if (promoFilter) {
      onPromoFilterChange?.(false);
    }
  };

  if (!tags.length && !showPromoFilter) {
    return (
      <Box sx={{ alignSelf: 'stretch', ...sx }}>
        <Typography variant="body2" color="text.secondary">
          {numProducts} {numProducts === 1 ? 'item' : 'itens'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch', gap: 1, ...sx }}>
      <Box
        title="Filtros de tags"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}
      >
        {showPromoFilter && (
          <Chip
            label="Em promoção"
            size="small"
            variant={promoFilter ? 'filled' : 'outlined'}
            color={promoFilter ? 'primary' : 'secondary'}
            onClick={() => onPromoFilterChange?.(!promoFilter)}
            sx={{ mr: 'auto' }}
          />
        )}
        <Typography variant="body2">
          {numProducts} {numProducts === 1 ? 'item' : 'itens'}
        </Typography>
        {hasActiveFilters && (
          <Button
            size="small"
            onClick={handleClearFilters}
            sx={clearFiltersButtonSx}
          >
            Limpar filtros
          </Button>
        )}
        {tags.length > 0 && (
          <Badge badgeContent={tagFilter.length} color="primary" sx={{ ml: 1 }}>
            <IconButton
              size="small"
              aria-label="Filtrar por tags"
              aria-expanded={showFilters}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FilterList />
            </IconButton>
          </Badge>
        )}
      </Box>

      {tags.length > 0 && (
        <Collapse in={showFilters}>
          <Box sx={{ pb: 1.5 }}>
          <TextField
            size="small"
            placeholder="Buscar tag..."
            value={chipSearch}
            onChange={(event) => setChipSearch(event.target.value)}
            fullWidth
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: layoutCardRadius,
              },
            }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start' }}>
            {visibleTags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                variant={isTagSelected(tag) ? 'contained' : 'outlined'}
                color={isTagSelected(tag) ? 'primary' : 'secondary'}
                onClick={() => handleTagClick(tag)}
              />
            ))}
            {visibleTags.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Nenhuma tag encontrada.
              </Typography>
            )}
          </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default ProductsTagFilters;
